<template>
  <div>
    <v-container class="main-container">
      <div>
        <!-- Header -->
        <v-row class="mb-6">
          <v-col cols="12">
            <v-card class="header-card pa-5 elevation-4">
              <div class="d-flex align-center">
                <v-avatar size="56" color="primary" class="mr-4">
                  <v-icon color="white" size="32">mdi-cash-multiple</v-icon>
                </v-avatar>
                <div>
                  <h1 class="text-h4 font-weight-bold mb-1">Remittance Management</h1>
                  <p class="text-subtitle-1 text--secondary mb-0">Track and manage remittances for shipped orders</p>
                </div>
              </div>
            </v-card>
          </v-col>
        </v-row>

        <!-- Search and Filter Section -->
        <v-row class="mb-5">
          <v-col cols="12">
            <v-card class="search-card pa-4 elevation-2">
              <v-row align="center" dense>
                <v-col cols="12" sm="6" md="4">
                  <v-text-field
                    v-model="searchQuery"
                    label="Search (Name, Order ID, Tracking)"
                    prepend-inner-icon="mdi-magnify"
                    outlined
                    dense
                    clearable
                    @keyup.enter="searchFunc"
                    @click:clear="clearSearch"
                    hide-details
                  ></v-text-field>
                </v-col>
                <v-col cols="12" sm="6" md="4">
                  <v-text-field
                    v-model="remittanceFilter"
                    label="Remittance Number"
                    prepend-inner-icon="mdi-cash-check"
                    outlined
                    dense
                    clearable
                    @keyup.enter="searchFunc"
                    @click:clear="clearRemittanceFilter"
                    hide-details
                  ></v-text-field>
                </v-col>
                <v-col cols="12" sm="6" md="4">
                  <v-select
                    v-model="statusFilter"
                    :items="statusOptions"
                    label="Remittance Status"
                    prepend-inner-icon="mdi-filter-variant"
                    outlined
                    dense
                    clearable
                    @change="searchFunc"
                    hide-details
                  ></v-select>
                </v-col>
              </v-row>
              <v-row align="center" dense class="mt-2">
                <v-col cols="12" sm="6" md="2">
                  <v-select
                    v-model="dateFilterType"
                    :items="dateFilterTypeOptions"
                    label="Date Type"
                    prepend-inner-icon="mdi-calendar-clock"
                    outlined
                    dense
                    @change="searchFunc"
                    hide-details
                  ></v-select>
                </v-col>
                <v-col cols="12" sm="6" md="2">
                  <v-menu
                    v-model="startDateMenu"
                    :close-on-content-click="false"
                    transition="scale-transition"
                    offset-y
                    min-width="auto"
                  >
                    <template v-slot:activator="{ on, attrs }">
                      <v-text-field
                        v-model="startDate"
                        label="From Date"
                        prepend-inner-icon="mdi-calendar"
                        readonly
                        outlined
                        dense
                        clearable
                        v-bind="attrs"
                        v-on="on"
                        @click:clear="clearStartDate"
                        hide-details
                      ></v-text-field>
                    </template>
                    <v-date-picker
                      v-model="startDate"
                      @input="startDateMenu = false; searchFunc()"
                    ></v-date-picker>
                  </v-menu>
                </v-col>
                <v-col cols="12" sm="6" md="2">
                  <v-menu
                    v-model="endDateMenu"
                    :close-on-content-click="false"
                    transition="scale-transition"
                    offset-y
                    min-width="auto"
                  >
                    <template v-slot:activator="{ on, attrs }">
                      <v-text-field
                        v-model="endDate"
                        label="To Date"
                        prepend-inner-icon="mdi-calendar"
                        readonly
                        outlined
                        dense
                        clearable
                        v-bind="attrs"
                        v-on="on"
                        @click:clear="clearEndDate"
                        hide-details
                      ></v-text-field>
                    </template>
                    <v-date-picker
                      v-model="endDate"
                      @input="endDateMenu = false; searchFunc()"
                    ></v-date-picker>
                  </v-menu>
                </v-col>
                <v-col cols="12" sm="12" md="4" class="d-flex justify-end">
                  <v-btn color="primary" @click="searchFunc" depressed small class="mr-2">
                    <v-icon left small>mdi-magnify</v-icon>
                    Search
                  </v-btn>
                  <v-btn color="grey" @click="clearAllFilters" outlined small class="mr-2">
                    <v-icon left small>mdi-close</v-icon>
                    Clear
                  </v-btn>
                  <v-btn color="success" @click="exportToExcel" depressed small :disabled="filteredOrdersForExport.length === 0">
                    <v-icon left small>mdi-file-excel</v-icon>
                    Export
                  </v-btn>
                </v-col>
              </v-row>
            </v-card>
          </v-col>
        </v-row>

        <!-- Statistics Cards -->
        <v-row class="mb-6">
          <v-col cols="12" sm="4">
            <v-card class="stat-card stat-card-blue pa-5 text-center" elevation="4" hover>
              <v-avatar size="60" color="blue lighten-5" class="mb-3">
                <v-icon color="blue darken-2" size="32">mdi-package-variant-closed</v-icon>
              </v-avatar>
              <div class="text-h3 font-weight-bold mb-2">{{ totalOrders }}</div>
              <div class="text-body-1 font-weight-medium">Total Shipped Orders</div>
            </v-card>
          </v-col>
          <v-col cols="12" sm="4">
            <v-card class="stat-card stat-card-green pa-5 text-center" elevation="4" hover>
              <v-avatar size="60" color="green lighten-5" class="mb-3">
                <v-icon color="green darken-2" size="32">mdi-check-circle</v-icon>
              </v-avatar>
              <div class="text-h3 font-weight-bold mb-2">{{ ordersWithRemittance }}</div>
              <div class="text-body-1 font-weight-medium">Remittance Added</div>
            </v-card>
          </v-col>
          <v-col cols="12" sm="4">
            <v-card class="stat-card stat-card-orange pa-5 text-center" elevation="4" hover>
              <v-avatar size="60" color="orange lighten-5" class="mb-3">
                <v-icon color="orange darken-2" size="32">mdi-alert-circle</v-icon>
              </v-avatar>
              <div class="text-h3 font-weight-bold mb-2">{{ ordersPendingRemittance }}</div>
              <div class="text-body-1 font-weight-medium">Pending Remittance</div>
            </v-card>
          </v-col>
        </v-row>

        <!-- Loading State -->
        <v-row v-if="loading" class="mt-8">
          <v-col cols="12" class="text-center">
            <v-card class="pa-8 elevation-2">
              <v-icon size="48" color="primary" class="mb-4">mdi-cloud-download</v-icon>
              <p class="text-h6 text--secondary font-weight-medium mb-2">Loading orders...</p>
              <p class="text-body-2 text--secondary mb-4">{{ loadingMessage }}</p>
              
              <!-- Progress Bar -->
              <v-progress-linear
                :value="loadingProgress"
                color="primary"
                height="12"
                rounded
                striped
                class="mx-auto"
                style="max-width: 400px;"
              >
                <template v-slot:default>
                  <strong class="white--text text-caption">{{ Math.round(loadingProgress) }}%</strong>
                </template>
              </v-progress-linear>
              
              <p class="text-caption text--secondary mt-3" v-if="loadingDetails">
                {{ loadingDetails }}
              </p>
            </v-card>
          </v-col>
        </v-row>

        <!-- Orders Grid -->
        <v-row v-else-if="orders.length > 0">
          <v-col
            v-for="order in orders"
            :key="order.id"
            cols="12"
            sm="6"
            md="4"
            lg="3"
          >
            <v-card
              class="order-card"
              elevation="2"
              hover
              @click="openViewModal(order)"
              :class="order.remittance ? 'order-card-completed' : 'order-card-pending'"
            >
              <div class="d-flex align-center justify-space-between pa-3" :class="order.remittance ? 'green lighten-5' : 'orange lighten-5'">
                <span class="text-subtitle-2 font-weight-bold">Order #{{ order.id }}</span>
                <v-chip
                  :color="order.remittance ? 'success' : 'warning'"
                  x-small
                  label
                >
                  {{ order.remittance ? 'Done' : 'Pending' }}
                </v-chip>
              </div>
              
              <v-card-text class="pa-3 pt-2">
                <div class="text-body-2 font-weight-medium mb-1">{{ formatCustomerName(order.billing) }}</div>
                <div class="d-flex justify-space-between text-caption text--secondary">
                  <span>₹{{ order.total }}</span>
                  <span>{{ formatDate(getShippingDate(order)) }}</span>
                </div>
                <div class="text-caption text--secondary mt-1" v-if="getTrackingId(order)">
                  <v-icon x-small class="mr-1">mdi-truck-outline</v-icon>{{ getTrackingId(order) }}
                </div>
              </v-card-text>

              <v-divider></v-divider>

              <v-card-actions class="pa-2">
                <v-btn
                  color="grey"
                  x-small
                  text
                  @click.stop="markAsResolved(order)"
                >
                  Resolve
                </v-btn>
                <v-spacer></v-spacer>
                <v-btn
                  :color="order.remittance ? 'success' : 'primary'"
                  x-small
                  depressed
                  @click.stop="openRemittanceModal(order)"
                >
                  {{ order.remittance ? 'Edit' : 'Add' }}
                </v-btn>
              </v-card-actions>
            </v-card>
          </v-col>
        </v-row>

        <!-- Empty State -->
        <v-row v-else class="mt-8">
          <v-col cols="12" class="text-center">
            <v-card class="pa-10 elevation-2">
              <v-avatar size="120" color="grey lighten-3" class="mb-4">
                <v-icon size="64" color="grey lighten-1">mdi-package-variant-closed-remove</v-icon>
              </v-avatar>
              <p class="text-h5 mt-4 font-weight-medium text--secondary">No shipped orders found</p>
              <p class="text-body-1 text--secondary mt-2">Orders with "shipped" status will appear here</p>
              <v-btn color="primary" class="mt-4" @click="fetchOrders" large>
                <v-icon left>mdi-refresh</v-icon>
                Refresh Orders
              </v-btn>
            </v-card>
          </v-col>
        </v-row>

        <!-- Pagination -->
        <v-row v-if="!loading && orders.length > 0" class="mt-4">
          <v-col cols="12" class="d-flex justify-center">
            <v-pagination
              v-model="options.page"
              :length="totalPages"
              :total-visible="7"
            ></v-pagination>
          </v-col>
        </v-row>
      </div>

      <!-- View Order Details Modal -->
      <v-dialog v-model="viewDialog" max-width="500">
        <v-card v-if="viewOrder">
          <div class="pa-4" :class="viewOrder.remittance ? 'green lighten-4' : 'orange lighten-4'">
            <div class="d-flex align-center justify-space-between">
              <span class="text-h6 font-weight-bold">Order #{{ viewOrder.id }}</span>
              <v-chip :color="viewOrder.remittance ? 'success' : 'warning'" small label>
                {{ viewOrder.remittance ? 'Remittance Done' : 'Pending' }}
              </v-chip>
            </div>
          </div>

          <v-card-text class="pa-4">
            <!-- Customer Info -->
            <div class="mb-4">
              <div class="text-overline text--secondary mb-1">Customer</div>
              <div class="text-body-1 font-weight-medium">{{ formatCustomerName(viewOrder.billing) }}</div>
              <div class="text-body-2 text--secondary">{{ viewOrder.billing?.email }}</div>
              <div class="text-body-2 text--secondary">{{ viewOrder.billing?.phone }}</div>
            </div>

            <v-divider class="mb-4"></v-divider>

            <!-- Order Info -->
            <v-row dense>
              <v-col cols="6">
                <div class="text-overline text--secondary">Amount</div>
                <div class="text-body-1 font-weight-bold">₹{{ viewOrder.total }}</div>
              </v-col>
              <v-col cols="6">
                <div class="text-overline text--secondary">Items</div>
                <div class="text-body-1">{{ viewOrder.line_items?.length }} item(s)</div>
              </v-col>
              <v-col cols="6" class="mt-2">
                <div class="text-overline text--secondary">Shipped Date</div>
                <div class="text-body-2">{{ formatDate(getShippingDate(viewOrder)) }}</div>
              </v-col>
              <v-col cols="6" class="mt-2">
                <div class="text-overline text--secondary">Tracking ID</div>
                <div class="text-body-2">{{ getTrackingId(viewOrder) || 'N/A' }}</div>
              </v-col>
            </v-row>

            <!-- Remittance Info if exists -->
            <template v-if="viewOrder.remittance">
              <v-divider class="my-4"></v-divider>
              <div class="green lighten-5 pa-3 rounded">
                <div class="text-overline success--text mb-2">Remittance Details</div>
                <v-row dense>
                  <v-col cols="6">
                    <div class="text-caption text--secondary">Number</div>
                    <div class="text-body-2 font-weight-medium">{{ viewOrder.remittance.number }}</div>
                  </v-col>
                  <v-col cols="6">
                    <div class="text-caption text--secondary">Amount</div>
                    <div class="text-body-2 font-weight-medium">₹{{ viewOrder.remittance.amount }}</div>
                  </v-col>
                  <v-col cols="6" class="mt-2">
                    <div class="text-caption text--secondary">Date</div>
                    <div class="text-body-2">{{ formatDate(viewOrder.remittance.date) }}</div>
                  </v-col>
                  <v-col cols="6" class="mt-2" v-if="viewOrder.remittance.notes">
                    <div class="text-caption text--secondary">Notes</div>
                    <div class="text-body-2">{{ viewOrder.remittance.notes }}</div>
                  </v-col>
                </v-row>
              </div>
            </template>
          </v-card-text>

          <v-divider></v-divider>

          <v-card-actions class="pa-3">
            <v-btn text @click="viewDialog = false">Close</v-btn>
            <v-spacer></v-spacer>
            <v-btn color="grey" outlined small @click="viewDialog = false; markAsResolved(viewOrder)">
              Resolve
            </v-btn>
            <v-btn :color="viewOrder.remittance ? 'success' : 'primary'" depressed small @click="viewDialog = false; openRemittanceModal(viewOrder)">
              {{ viewOrder.remittance ? 'Edit Remittance' : 'Add Remittance' }}
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <!-- Remittance Modal -->
      <v-dialog v-model="remittanceDialog" max-width="650" persistent>
        <v-card class="elevation-8">
          <v-card-title class="primary white--text pa-5">
            <v-icon left color="white" size="28">mdi-cash-multiple</v-icon>
            <span class="text-h6 font-weight-bold">{{ selectedOrder?.remittance ? 'Edit' : 'Add' }} Remittance Details</span>
          </v-card-title>

          <v-card-text class="pt-6 px-6">
            <v-form ref="remittanceForm" v-model="formValid">
              <!-- Order Information -->
              <v-alert type="info" text outlined class="mb-4">
                <div class="d-flex align-center">
                  <div>
                    <div class="font-weight-bold">Order #{{ selectedOrder?.id }}</div>
                    <div class="text-caption">{{ formatCustomerName(selectedOrder?.billing) }}</div>
                    <div class="text-caption">Order Amount: ₹{{ selectedOrder?.total }}</div>
                  </div>
                </div>
              </v-alert>

              <!-- Remittance Number -->
              <v-text-field
                v-model="remittanceData.number"
                label="Remittance Number *"
                outlined
                dense
                :rules="[v => !!v || 'Remittance number is required']"
                prepend-inner-icon="mdi-identifier"
                placeholder="Enter remittance/transaction number"
                class="mb-2"
              ></v-text-field>

              <!-- Remittance Amount -->
              <v-text-field
                v-model.number="remittanceData.amount"
                label="Remittance Amount *"
                outlined
                dense
                type="number"
                :rules="[
                  v => !!v || 'Amount is required',
                  v => v > 0 || 'Amount must be greater than 0'
                ]"
                prepend-inner-icon="mdi-currency-inr"
                prefix="₹"
                placeholder="Enter remittance amount"
                class="mb-2"
              ></v-text-field>

              <!-- Remittance Date -->
              <v-menu
                v-model="dateMenu"
                :close-on-content-click="false"
                transition="scale-transition"
                offset-y
                min-width="auto"
              >
                <template v-slot:activator="{ on, attrs }">
                  <v-text-field
                    v-model="remittanceData.date"
                    label="Remittance Date *"
                    prepend-inner-icon="mdi-calendar"
                    readonly
                    outlined
                    dense
                    v-bind="attrs"
                    v-on="on"
                    :rules="[v => !!v || 'Date is required']"
                    class="mb-2"
                  ></v-text-field>
                </template>
                <v-date-picker
                  v-model="remittanceData.date"
                  @input="dateMenu = false"
                  :max="new Date().toISOString().substr(0, 10)"
                ></v-date-picker>
              </v-menu>

              <!-- Notes (Optional) -->
              <v-textarea
                v-model="remittanceData.notes"
                label="Notes (Optional)"
                outlined
                dense
                rows="3"
                prepend-inner-icon="mdi-note-text"
                placeholder="Add any additional notes..."
              ></v-textarea>
            </v-form>
          </v-card-text>

          <v-divider></v-divider>

          <v-card-actions class="pa-5">
            <v-spacer></v-spacer>
            <v-btn
              outlined
              large
              @click="closeRemittanceModal"
              :disabled="saving"
            >
              Cancel
            </v-btn>
            <v-btn
              color="primary"
              large
              depressed
              @click="saveRemittance"
              :loading="saving"
              :disabled="!formValid || saving"
            >
              <v-icon left>mdi-content-save</v-icon>
              Save Remittance Details
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <!-- Loading Dialog -->
      <v-dialog v-model="saving" persistent max-width="300">
        <v-card>
          <v-card-text class="text-center pa-6">
            <v-progress-circular
              indeterminate
              color="primary"
              size="50"
            ></v-progress-circular>
            <p class="mt-4 mb-0">Saving remittance details...</p>
          </v-card-text>
        </v-card>
      </v-dialog>
    </v-container>
  </div>
</template>

<script>
import axios from "axios";
import moment from "moment";
import * as XLSX from "xlsx";

export default {
  name: "Remittance",
  data() {
    return {
      ENDPOINT: process.env.VUE_APP_ENDPOINT,
      orders: [],
      allOrders: [], // Store all fetched orders for client-side filtering
      filteredOrdersForExport: [], // Store filtered orders for export
      loading: false,
      loadingProgress: 0,
      loadingMessage: "Initializing...",
      loadingDetails: "",
      saving: false,
      totalOrders: 0,
      totalPages: 1,
      searchQuery: "",
      remittanceFilter: "",
      startDate: "",
      endDate: "",
      startDateMenu: false,
      endDateMenu: false,
      dateFilterType: "shipping",
      dateFilterTypeOptions: [
        { text: "Shipping Date", value: "shipping" },
        { text: "Remittance Date", value: "remittance" },
      ],
      statusFilter: "",
      statusOptions: [
        { text: "All", value: "" },
        { text: "Remittance Added", value: "added" },
        { text: "Pending", value: "pending" },
      ],
      options: {
        page: 1,
        itemsPerPage: 20,
      },
      remittanceDialog: false,
      viewDialog: false,
      viewOrder: null,
      dateMenu: false,
      formValid: false,
      selectedOrder: null,
      remittanceData: {
        number: "",
        amount: "",
        date: "",
        notes: "",
      },
    };
  },
  computed: {
    ordersWithRemittance() {
      return this.allOrders.filter(order => order.remittance).length;
    },
    ordersPendingRemittance() {
      return this.allOrders.filter(order => !order.remittance).length;
    },
  },
  mounted() {
    this.fetchOrders();
  },
  watch: {
    "options.page"() {
      this.filterAndPaginateOrders();
    },
    startDate() {
      this.options.page = 1;
      this.filterAndPaginateOrders();
    },
    endDate() {
      this.options.page = 1;
      this.filterAndPaginateOrders();
    },
    statusFilter() {
      this.options.page = 1;
      this.filterAndPaginateOrders();
    },
    dateFilterType() {
      this.options.page = 1;
      this.filterAndPaginateOrders();
    },
  },
  methods: {
    async fetchOrders() {
      this.loading = true;
      this.loadingProgress = 0;
      this.loadingMessage = "Connecting to server...";
      this.loadingDetails = "";
      
      try {
        // Fetch first page to get total pages
        this.loadingMessage = "Fetching order information...";
        this.loadingProgress = 5;
        
        const firstResponse = await axios.get(
          `${this.ENDPOINT}/orders?page=1&per_page=100&status=shipped&payment_method=cod`
        );
        
        let allOrders = firstResponse.data.orders;
        const totalPages = Number(firstResponse.data.totalPages);
        
        this.loadingProgress = 15;
        this.loadingMessage = "Processing orders...";
        this.loadingDetails = `Found ${totalPages} page(s) of orders`;

        // Fetch remaining pages in batches of 5 to avoid overwhelming server
        if (totalPages > 1) {
          const batchSize = 5;
          let pagesLoaded = 1;
          
          for (let startPage = 2; startPage <= totalPages; startPage += batchSize) {
            const endPage = Math.min(startPage + batchSize - 1, totalPages);
            const pagePromises = [];
            
            for (let page = startPage; page <= endPage; page++) {
              pagePromises.push(
                axios.get(
                  `${this.ENDPOINT}/orders?page=${page}&per_page=100&status=shipped&payment_method=cod`
                )
              );
            }

            // Fetch batch in parallel
            const responses = await Promise.all(pagePromises);
            responses.forEach(response => {
              allOrders = allOrders.concat(response.data.orders);
              pagesLoaded++;
            });
            
            // Update progress (15% to 90% range for fetching)
            const progressPercent = 15 + ((pagesLoaded / totalPages) * 75);
            this.loadingProgress = Math.min(progressPercent, 90);
            this.loadingMessage = "Downloading orders...";
            this.loadingDetails = `Loaded ${pagesLoaded} of ${totalPages} pages (${allOrders.length} orders)`;
          }
        }

        this.loadingProgress = 95;
        this.loadingMessage = "Finalizing...";
        this.loadingDetails = `Processing ${allOrders.length} orders`;
        
        this.allOrders = allOrders;
        this.filterAndPaginateOrders();
        
        this.loadingProgress = 100;
        this.loadingMessage = "Complete!";
      } catch (error) {
        console.error("Error fetching orders:", error);
        this.$swal({
          icon: "error",
          title: "Error",
          text: "Failed to fetch orders. Please try again.",
        });
      } finally {
        this.loading = false;
        this.loadingProgress = 0;
        this.loadingMessage = "";
        this.loadingDetails = "";
      }
    },

    filterAndPaginateOrders() {
      let filteredOrders = this.allOrders;

      // Apply search filter if search query exists
      if (this.searchQuery && this.searchQuery.trim()) {
        const query = this.searchQuery.trim().toLowerCase();
        filteredOrders = filteredOrders.filter((order) => {
          // Search in order ID
          if (order.id.toString().includes(query)) return true;
          
          // Search in tracking ID from meta_data
          const trackingId = order.meta_data.find(
            (meta) => meta.key === "tracking_id"
          )?.value;
          if (trackingId && trackingId.toLowerCase().includes(query)) return true;
          
          // Search in customer name
          const customerName = `${order.billing.first_name} ${order.billing.last_name}`.toLowerCase();
          if (customerName.includes(query)) return true;
          
          return false;
        });
      }

      // Apply remittance number filter
      if (this.remittanceFilter && this.remittanceFilter.trim()) {
        const remitQuery = this.remittanceFilter.trim().toLowerCase();
        filteredOrders = filteredOrders.filter((order) => {
          if (order.remittance && order.remittance.number) {
            return order.remittance.number.toLowerCase().includes(remitQuery);
          }
          return false;
        });
      }

      // Apply date range filter
      if (this.startDate || this.endDate) {
        filteredOrders = filteredOrders.filter((order) => {
          let dateToCheck = null;
          
          if (this.dateFilterType === "shipping") {
            // Use shipping date from meta_data
            const shippingDate = order.meta_data.find(
              (meta) => meta.key === "shipping_date"
            )?.value;
            if (shippingDate) {
              dateToCheck = shippingDate.substring(0, 10); // Get YYYY-MM-DD portion
            }
          } else if (this.dateFilterType === "remittance") {
            // Use remittance date
            if (order.remittance && order.remittance.date) {
              dateToCheck = order.remittance.date.substring(0, 10); // Get YYYY-MM-DD portion
            }
          }
          
          if (dateToCheck) {
            if (this.startDate && this.endDate) {
              return dateToCheck >= this.startDate && dateToCheck <= this.endDate;
            } else if (this.startDate) {
              return dateToCheck >= this.startDate;
            } else if (this.endDate) {
              return dateToCheck <= this.endDate;
            }
          }
          return false;
        });
      }

      // Apply status filter (remittance added or pending)
      if (this.statusFilter) {
        filteredOrders = filteredOrders.filter((order) => {
          if (this.statusFilter === "added") {
            return order.remittance && order.remittance.number;
          } else if (this.statusFilter === "pending") {
            return !order.remittance || !order.remittance.number;
          }
          return true;
        });
      }

      // Update totals
      this.totalOrders = filteredOrders.length;
      this.totalPages = Math.ceil(filteredOrders.length / this.options.itemsPerPage);

      // Store filtered orders for export (before pagination)
      this.filteredOrdersForExport = filteredOrders;

      // Apply pagination
      const { page, itemsPerPage } = this.options;
      const startIndex = (page - 1) * itemsPerPage;
      const endIndex = startIndex + itemsPerPage;
      this.orders = filteredOrders.slice(startIndex, endIndex);
    },

    searchFunc() {
      this.options.page = 1;
      this.filterAndPaginateOrders();
    },

    clearSearch() {
      this.searchQuery = "";
      this.options.page = 1;
      this.filterAndPaginateOrders();
    },

    clearRemittanceFilter() {
      this.remittanceFilter = "";
      this.options.page = 1;
      this.filterAndPaginateOrders();
    },

    clearStartDate() {
      this.startDate = "";
      this.options.page = 1;
      this.filterAndPaginateOrders();
    },

    clearEndDate() {
      this.endDate = "";
      this.options.page = 1;
      this.filterAndPaginateOrders();
    },

    clearStatusFilter() {
      this.statusFilter = "";
      this.options.page = 1;
      this.filterAndPaginateOrders();
    },

    clearAllFilters() {
      this.searchQuery = "";
      this.remittanceFilter = "";
      this.startDate = "";
      this.endDate = "";
      this.statusFilter = "";
      this.dateFilterType = "shipping";
      this.options.page = 1;
      this.filterAndPaginateOrders();
    },

    exportToExcel() {
      if (this.filteredOrdersForExport.length === 0) {
        this.$swal("No Data", "No orders to export", "warning");
        return;
      }

      // Prepare data for Excel
      const exportData = this.filteredOrdersForExport.map((order) => {
        const trackingId = order.meta_data?.find(
          (meta) => meta.key === "tracking_id"
        )?.value || "";
        const shippingDate = order.meta_data?.find(
          (meta) => meta.key === "shipping_date"
        )?.value || order.date_modified || "";

        return {
          "Order ID": order.id,
          "Customer Name": `${order.billing?.first_name || ""} ${order.billing?.last_name || ""}`.trim(),
          "Email": order.billing?.email || "",
          "Phone": order.billing?.phone || "",
          "Order Total": order.total,
          "Currency": order.currency,
          "Order Status": order.status,
          "Tracking ID": trackingId,
          "Shipping Date": shippingDate ? moment(shippingDate).format("DD-MMM-YYYY") : "",
          "Remittance Number": order.remittance?.number || "",
          "Remittance Amount": order.remittance?.amount || "",
          "Remittance Date": order.remittance?.date ? moment(order.remittance.date).format("DD-MMM-YYYY") : "",
          "Remittance Notes": order.remittance?.notes || "",
          "Remittance Status": order.remittance?.number ? "Added" : "Pending",
        };
      });

      // Create worksheet
      const worksheet = XLSX.utils.json_to_sheet(exportData);

      // Set column widths
      worksheet["!cols"] = [
        { wch: 10 }, // Order ID
        { wch: 25 }, // Customer Name
        { wch: 30 }, // Email
        { wch: 15 }, // Phone
        { wch: 12 }, // Order Total
        { wch: 10 }, // Currency
        { wch: 12 }, // Order Status
        { wch: 20 }, // Tracking ID
        { wch: 15 }, // Shipping Date
        { wch: 20 }, // Remittance Number
        { wch: 15 }, // Remittance Amount
        { wch: 15 }, // Remittance Date
        { wch: 30 }, // Remittance Notes
        { wch: 15 }, // Remittance Status
      ];

      // Create workbook and add worksheet
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Remittance Orders");

      // Generate filename with date
      const filename = `remittance-orders-${moment().format("YYYY-MM-DD-HHmm")}.xlsx`;

      // Download the file
      XLSX.writeFile(workbook, filename);

      this.$swal(
        "Export Successful",
        `Exported ${this.filteredOrdersForExport.length} orders to ${filename}`,
        "success"
      );
    },

    formatCustomerName(billing) {
      if (!billing) return "N/A";
      return `${billing.first_name} ${billing.last_name}`;
    },

    formatDate(date) {
      if (!date) return "N/A";
      return moment(date).format("DD MMM YYYY");
    },

    getShippingDate(order) {
      const shippingDateMeta = order.meta_data?.find(
        (meta) => meta.key === "shipping_date"
      );
      return shippingDateMeta?.value || order.date_modified || order.date_created;
    },

    getTrackingId(order) {
      const trackingMeta = order.meta_data?.find(
        (meta) => meta.key === "tracking_id"
      );
      return trackingMeta?.value || "";
    },

    openViewModal(order) {
      this.viewOrder = order;
      this.viewDialog = true;
    },

    openRemittanceModal(order) {
      this.selectedOrder = order;
      
      // Pre-fill form if remittance data exists
      if (order.remittance) {
        this.remittanceData = {
          number: order.remittance.number || "",
          amount: order.remittance.amount || order.total,
          date: order.remittance.date || "",
          notes: order.remittance.notes || "",
        };
      } else {
        // Reset form with default values
        this.remittanceData = {
          number: "",
          amount: order.total,
          date: new Date().toISOString().substr(0, 10),
          notes: "",
        };
      }
      
      this.remittanceDialog = true;
    },

    closeRemittanceModal() {
      this.remittanceDialog = false;
      this.selectedOrder = null;
      this.remittanceData = {
        number: "",
        amount: "",
        date: "",
        notes: "",
      };
      if (this.$refs.remittanceForm) {
        this.$refs.remittanceForm.reset();
      }
    },

    async saveRemittance() {
      if (!this.$refs.remittanceForm.validate()) {
        return;
      }

      this.saving = true;
      try {
        const orderId = this.selectedOrder.id;
        
        // Update order with remittance data via meta_data
        const response = await axios.put(
          `${this.ENDPOINT}/orders/${orderId}/remittance`,
          {
            remittance: this.remittanceData,
          }
        );

        this.$swal({
          icon: "success",
          title: "Success",
          text: "Remittance details saved successfully!",
          timer: 2000,
          showConfirmButton: false,
        });

        // Update local order data
        const updateOrderRemittance = (order) => {
          if (order.id === orderId) {
            order.remittance = { ...this.remittanceData };
          }
        };
        
        this.orders.forEach(updateOrderRemittance);
        this.allOrders.forEach(updateOrderRemittance);

        this.closeRemittanceModal();
      } catch (error) {
        console.error("Error saving remittance:", error);
        this.$swal({
          icon: "error",
          title: "Error",
          text: "Failed to save remittance details. Please try again.",
        });
      } finally {
        this.saving = false;
      }
    },

    async markAsResolved(order) {
      const result = await this.$swal({
        title: "Mark as Resolved?",
        html: `<div style="text-align: left;">
          <p><strong>Order #${order.id}</strong></p>
          <p class="text-body-2">This will mark the order as resolved.</p>
          <div class="mt-3 pa-3" style="background-color: #fff3cd; border-left: 4px solid #ffc107; border-radius: 4px;">
            <div style="display: flex; align-items: start;">
              <svg style="width: 20px; height: 20px; margin-right: 8px; flex-shrink: 0; color: #856404;" viewBox="0 0 24 24">
                <path fill="currentColor" d="M13,14H11V10H13M13,18H11V16H13M1,21H23L12,2L1,21Z" />
              </svg>
              <div style="color: #856404;">
                <strong>Warning:</strong> Once resolved, you will not be able to add or edit remittance details for this order.
              </div>
            </div>
          </div>
        </div>`,
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#ff9800",
        cancelButtonColor: "#9e9e9e",
        confirmButtonText: "Yes, Mark as Resolved",
        cancelButtonText: "Cancel",
        customClass: {
          popup: 'swal-wide'
        }
      });

      if (result.isConfirmed) {
        this.loading = true;
        try {
          await axios.post(`${this.ENDPOINT}/update-order-status`, {
            orderId: order.id,
            status: "resolved",
          });

          this.$swal({
            icon: "success",
            title: "Resolved",
            text: `Order #${order.id} has been marked as resolved.`,
            timer: 2000,
            showConfirmButton: false,
          });

          // Remove the order from local arrays since it's no longer "shipped"
          this.allOrders = this.allOrders.filter(o => o.id !== order.id);
          this.filterAndPaginateOrders();
        } catch (error) {
          console.error("Error marking order as resolved:", error);
          this.$swal({
            icon: "error",
            title: "Error",
            text: "Failed to mark order as resolved. Please try again.",
          });
        } finally {
          this.loading = false;
        }
      }
    },
  },
};
</script>

<style scoped>
.main-container {
  max-width: 1400px;
}

.header-card {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
}

.header-card h1,
.header-card p {
  color: white !important;
}

.search-card {
  background: linear-gradient(to right, #f8f9fa, #ffffff);
}

.search-field >>> .v-input__control {
  font-size: 1rem;
}

.stat-card {
  cursor: pointer;
  transition: all 0.3s ease;
  border-radius: 12px !important;
  position: relative;
  overflow: hidden;
}

.stat-card::before {
  content: '';
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.3), transparent);
  transition: left 0.5s;
}

.stat-card:hover::before {
  left: 100%;
}

.stat-card:hover {
  transform: translateY(-8px) scale(1.02);
}

.stat-card-blue {
  background: linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%);
  border-left: 4px solid #1976d2;
}

.stat-card-green {
  background: linear-gradient(135deg, #e8f5e9 0%, #c8e6c9 100%);
  border-left: 4px solid #388e3c;
}

.stat-card-orange {
  background: linear-gradient(135deg, #fff3e0 0%, #ffe0b2 100%);
  border-left: 4px solid #f57c00;
}

.order-card {
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  height: 100%;
  border-radius: 12px !important;
  overflow: hidden;
}

.order-card:hover {
  transform: translateY(-6px);
  box-shadow: 0 12px 24px rgba(0, 0, 0, 0.2) !important;
}

.order-card-completed {
  border-left: 4px solid #4caf50;
}

.order-card-pending {
  border-left: 4px solid #ff9800;
}

.card-header {
  background: linear-gradient(to right, #f5f5f5, #fafafa);
}

.card-header-completed {
  background: linear-gradient(to right, #e8f5e9, #f1f8e9);
}

.card-header-pending {
  background: linear-gradient(to right, #fff3e0, #fff8e1);
}

.info-row {
  display: flex;
  align-items: center;
  padding: 4px 0;
}

.remittance-info {
  background: linear-gradient(135deg, #e8f5e9 0%, #f1f8e9 100%);
  border-left: 3px solid #4caf50;
}

.v-pagination {
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  border-radius: 8px;
  padding: 8px;
}

/* Swal wide class */
.swal-wide {
  width: 600px !important;
}
</style>
